import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { BcRestApi } from '../../../../../common/api/rest-api';
import { KeyboardKeyCodes } from '../../../../../common/common/constants/keyboard-key-codes';
import { WorkItemListenerService } from '../../../../../common/workitems/workitem-listener.service';
import { BcAnalytics } from '../../../../../common/analytics/analytics.service';
import { WorkItemTypes } from '../../../../../common/workitems/constants/workitem-types';
import { bcEnums } from '../../../../../common/common/constants/boldchat-enums';
import { cloneDeep } from 'lodash';
import { filter, take } from 'rxjs/operators';
import { WorkItem } from '../../../../../common/workitems/interfaces/workitem-base.interface';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { Subscription } from 'rxjs';

@Component({
  selector: 'workitem-answer-next',
  templateUrl: './workitem-answer-next.component.html',
  encapsulation: ViewEncapsulation.None
})
export class WorkitemAnswerNextComponent implements OnInit, OnDestroy {
  @Input() workItemType: WorkItemTypes;
  @Output() itemSelected = new EventEmitter<any>();

  public answerNextInProgress = false;
  public itemQueue: any[] = [];
  public itemQueueClone: any[];

  private maxRetryCount: number = 5;
  private correctWorkItemTypeErrorMessage: string = 'Please specify the correct work item type.';
  private insertQueueItemSubscription: Subscription;
  private removeQueueItemSubscription: Subscription;
  private takeNextSubscription: Subscription;

  constructor(public bcRestApi: BcRestApi,
              private bcAnalytics: BcAnalytics,
              private bcWorkItemListener: WorkItemListenerService,
              private rxEmitter: RxEmitter) {
  }

  get modelIdName(): string {
    switch (this.workItemType) {
      case WorkItemTypes.CHAT:
        return 'ChatID';
      case WorkItemTypes.EMAIL:
        return 'EmailThreadID';
      case WorkItemTypes.SMS:
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
        return 'TextChatID';
      default:
        throw new Error(this.correctWorkItemTypeErrorMessage);
    }
  }

  get friendlyWorkItemTypeName() {
    switch (this.workItemType) {
      case WorkItemTypes.CHAT:
        return 'chat';
      case WorkItemTypes.EMAIL:
        return 'email';
      case WorkItemTypes.SMS:
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
        return 'message';
      default:
        throw new Error(this.correctWorkItemTypeErrorMessage);
    }
  }

  restApiAnswerNextMethod(id) {
    switch (this.workItemType) {
      case WorkItemTypes.CHAT:
        return this.bcRestApi.answerNextChat(id);
      default:
        throw new Error(this.correctWorkItemTypeErrorMessage);
    }
  }

  ngOnInit(): void {
    this.insertQueueItemSubscription = this.bcWorkItemListener.observeAnswerableWorkItemsByLoginIdByWorkItemType(this.workItemType).inserted()
      .subscribe(queuedItem => this._onInsertQueueItem(queuedItem), console.error);

    this.removeQueueItemSubscription = this.bcWorkItemListener.observeAnswerableWorkItemsByLoginIdByWorkItemType(this.workItemType).deleted()
      .subscribe(queuedItem => this._onRemoveQueueItem(queuedItem), console.error);

    this.takeNextSubscription = this.rxEmitter.subscribe(MessageBusEvents.TakeNext, data => {
      if (this.workItemType === data.workItemType) {
        this._answerNext();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.insertQueueItemSubscription && this.insertQueueItemSubscription.unsubscribe) {
      this.insertQueueItemSubscription.unsubscribe();
    }
    if (this.removeQueueItemSubscription && this.removeQueueItemSubscription.unsubscribe) {
      this.removeQueueItemSubscription.unsubscribe();
    }
    if (this.takeNextSubscription && this.takeNextSubscription.unsubscribe) {
      this.takeNextSubscription.unsubscribe();
    }
  }

  isQueueEmpty(): boolean {
    return !this.itemQueue.length;
  }

  _onInsertQueueItem(queueItem) {
    this.itemQueue.push(queueItem);
  }

  _onRemoveQueueItem(queueItem) {
    const foundIndex = this.itemQueue.findIndex(item => item[this.modelIdName] === queueItem[this.modelIdName]);
    if (foundIndex !== -1) {
      this.itemQueue.splice(foundIndex, 1);
    }
  }

  _getNextItemFromQueue(retryCount) {
    if (!this.itemQueueClone.length) {
      return null;
    }

    return this.itemQueueClone[retryCount];
  }

  _subscribeToOpenItem(itemIdToAnswer: string): any {
    return this.bcWorkItemListener.createAcdBasedObservableForInsertByWorkItemType(this.workItemType).pipe(
      filter((workItem: WorkItem) => workItem.id === itemIdToAnswer),
      take(1)
    ).subscribe(
      (workItem) => this.itemSelected.emit(workItem),
      console.error
    );
  }

  _answerNext(retryCount: number = 0) {
    if (retryCount === 0) {
      this.itemQueueClone = cloneDeep(this.itemQueue);
    }
    const nextQueuedItem = this._getNextItemFromQueue(retryCount);
    if (!nextQueuedItem) {
      this.answerNextInProgress = false;
      return;
    }
    this.answerNextInProgress = true;

    const queuedItemIdToAnswer = nextQueuedItem[this.modelIdName];
    const openItemSubscription = this._subscribeToOpenItem(queuedItemIdToAnswer);

    this.restApiAnswerNextMethod(queuedItemIdToAnswer)
      .then(() => {
        this.answerNextInProgress = false;
        this.itemQueueClone = null;
      }, (error) => {
        if (openItemSubscription) {
          openItemSubscription.unsubscribe();
        }
        this._handleAnswerError(error, retryCount);
      });
  }

  _handleAnswerError(error: any, retryCount: number = 0) {
    if (retryCount > this.maxRetryCount) {
      this.answerNextInProgress = false;
      this.bcAnalytics.eventTrack('AnswerNextError', { type: this.workItemType, cause: error.status });
      return;
    }

    if (error.data && error.data[0]) {
      const errorResponse = error.data[0];
      if (errorResponse.code === bcEnums.assignNextCodes.ALREADY_ASSIGNED) {
        this.bcAnalytics.eventTrack('AnswerNext', { type: this.workItemType, origin: 'retry', queueSize: this.itemQueue.length });
        this._answerNext(retryCount + 1);
      }
    }
  }

  onAnswerNextClicked() {
    this.bcAnalytics.eventTrack('AnswerNext', { type: this.workItemType, origin: 'click', queueSize: this.itemQueue.length });
    this._answerNext();
  }

  onAnswerNextKeydown(event) {
    if (event.which !== KeyboardKeyCodes.ENTER) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.bcAnalytics.eventTrack('AnswerNext', { type: this.workItemType, origin: 'keydown', queueSize: this.itemQueue.length });
    this._answerNext();
  }
}
